import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface QuoteLineItem {
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface QuoteFormData {
  customerId: string;
  title: string;
  description: string;
  validUntil: string;
  notes: string;
}

interface UseQuoteFormProps {
  defaultLineItems: QuoteLineItem[];
  onSuccess: () => void;
  isBankQuote?: boolean;
  editingQuote?: any;
}

export const useQuoteForm = ({ defaultLineItems, onSuccess, isBankQuote = false, editingQuote }: UseQuoteFormProps) => {
  const [formData, setFormData] = useState<QuoteFormData>({
    customerId: editingQuote?.customer_id || "",
    title: editingQuote?.title || "",
    description: editingQuote?.description || "",
    validUntil: editingQuote?.valid_until || "",
    notes: editingQuote?.notes || "",
  });
  
  const [lineItems, setLineItems] = useState<QuoteLineItem[]>(
    editingQuote?.line_items || defaultLineItems
  );
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const updateFormData = (field: keyof QuoteFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-calculate cash price based on remaining balance when customer is selected
    if (field === 'customerId' && value && !isBankQuote) {
      // This would typically fetch customer data, but for now we'll assume it's available
      // In a real implementation, you'd fetch customer data here
      console.log('Customer selected, should auto-calculate cash price based on remaining balance');
    }
  };

  const updateLineItem = (index: number, field: keyof QuoteLineItem, value: string | number) => {
    const newLineItems = [...lineItems];
    newLineItems[index] = { ...newLineItems[index], [field]: value };
    
    if (field === 'quantity' || field === 'unit_price') {
      newLineItems[index].line_total = newLineItems[index].quantity * newLineItems[index].unit_price;
    }
    
    setLineItems(newLineItems);
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: "", quantity: 1, unit_price: 0, line_total: 0 }]);
  };

  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const subtotal = lineItems.reduce((sum, item) => sum + item.line_total, 0);
    const total = subtotal; // No tax calculation, just total equals subtotal
    return { subtotal, total };
  };

  const validateForm = (additionalRequiredFields: string[] = []) => {
    const requiredFields = ['customerId', 'title', ...additionalRequiredFields];
    return requiredFields.every(field => formData[field as keyof QuoteFormData]);
  };

  const submitQuote = async (additionalData?: any) => {
    setIsLoading(true);
    try {
      const { subtotal, total } = calculateTotals();

      const quoteData = {
        customer_id: formData.customerId,
        title: isBankQuote ? `[BANK] ${formData.title}` : formData.title,
        description: formData.description,
        subtotal,
        tax_rate: 0,
        tax_amount: 0,
        total_amount: total,
        valid_until: formData.validUntil || null,
        notes: formData.notes,
        status: 'draft',
        quote_number: editingQuote?.quote_number || '',
        ...additionalData
      };

      if (editingQuote) {
        // Update existing quote
        const { error: quoteError } = await supabase
          .from('quotes')
          .update(quoteData)
          .eq('id', editingQuote.id);

        if (quoteError) throw quoteError;

        // Delete existing line items
        const { error: deleteError } = await supabase
          .from('quote_line_items')
          .delete()
          .eq('quote_id', editingQuote.id);

        if (deleteError) throw deleteError;

        // Insert new line items
        const lineItemsToInsert = lineItems.map(item => ({
          quote_id: editingQuote.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_total: item.line_total
        }));

        const { error: lineItemsError } = await supabase
          .from('quote_line_items')
          .insert(lineItemsToInsert);

        if (lineItemsError) throw lineItemsError;

        toast({
          title: "Success",
          description: `${isBankQuote ? 'Bank quote' : 'Quote'} updated successfully`,
        });
      } else {
        // Create new quote
        const { data: quote, error: quoteError } = await supabase
          .from('quotes')
          .insert(quoteData)
          .select()
          .single();

        if (quoteError) throw quoteError;

        const lineItemsToInsert = lineItems.map(item => ({
          quote_id: quote.id,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          line_total: item.line_total
        }));

        const { error: lineItemsError } = await supabase
          .from('quote_line_items')
          .insert(lineItemsToInsert);

        if (lineItemsError) throw lineItemsError;

        toast({
          title: "Success",
          description: `${isBankQuote ? 'Bank quote' : 'Quote'} created successfully`,
        });
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving quote:', error);
      toast({
        title: "Error",
        description: `Failed to ${editingQuote ? 'update' : 'create'} ${isBankQuote ? 'bank quote' : 'quote'}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    formData,
    lineItems,
    isLoading,
    updateFormData,
    updateLineItem,
    addLineItem,
    removeLineItem,
    calculateTotals,
    validateForm,
    submitQuote,
  };
};